import { useState, useRef } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { STATUS_OPTIONS } from "@/lib/constants";
import { Loader2, Upload } from "lucide-react";

// Dialog ubah status massal pekerja terpilih (dengan keterangan & dokumen bukti)
export default function WorkersBulkStatusDialog({ open, onOpenChange, selected, onSaved }) {
  const [status, setStatus] = useState("PENSIUN");
  const [ket, setKet] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const docRef = useRef(null);

  const isResignPhk = ["RESIGN", "PHK"].includes(status);

  const submit = async () => {
    if (isResignPhk && !ket.trim()) {
      toast.error(`Keterangan wajib diisi untuk status ${status}`);
      return;
    }
    if (isResignPhk && !docFile) {
      toast.error(`Unggah dokumen bukti (evidence) untuk status ${status}`);
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("worker_ids", [...selected].join(","));
      fd.append("status", status);
      fd.append("keterangan_status", ket);
      if (docFile) fd.append("file", docFile);
      const res = await api.post("/workers/bulk/status", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Status ${res.data.updated} pekerja diubah menjadi ${status}`);
      onOpenChange(false);
      setKet("");
      setDocFile(null);
      if (docRef.current) docRef.current.value = "";
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah status massal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="bulk-status-dialog">
        <DialogHeader>
          <DialogTitle>Ubah Status {selected.size} Pekerja</DialogTitle>
          <DialogDescription>Status, keterangan, dan dokumen bukti yang sama akan diterapkan ke semua pekerja terpilih.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Status Baru</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-11" data-testid="bulk-status-select">
                <SelectValue placeholder="Pilih status..." />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} data-testid={`bulk-status-option-${o}`}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">
              Keterangan {isResignPhk && <span className="text-rose-500">*</span>}
            </Label>
            <Textarea value={ket} onChange={(e) => setKet(e.target.value)} rows={2}
              placeholder={`Alasan/keterangan (berlaku untuk semua pekerja terpilih)...`}
              data-testid="bulk-status-keterangan" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">
              Dokumen Bukti {isResignPhk && <span className="text-rose-500">*</span>}
            </Label>
            <input type="file" ref={docRef} accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setDocFile(e.target.files?.[0] || null)} className="hidden"
              data-testid="bulk-status-doc-input" />
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => docRef.current?.click()}
                data-testid="bulk-status-doc-pick"
                className="gap-2 border-slate-200 text-slate-700 hover:bg-slate-50">
                <Upload className="h-4 w-4" /> {docFile ? "Ganti Dokumen" : "Unggah Dokumen"}
              </Button>
              {docFile && (
                <span className="text-xs font-medium text-slate-700 truncate max-w-[200px]" data-testid="bulk-status-doc-name">
                  {docFile.name}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-400">Satu dokumen yang sama akan dilampirkan ke semua pekerja terpilih (PDF/JPG/PNG, maks 10 MB).</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
            data-testid="bulk-status-cancel">Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="bulk-status-submit"
            className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
